import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine, Reveal, Eyebrow } from "@/components/Reveal";
import { BLOGS } from "@/data/blogs";

const Meta = ({ post, light = false }) => (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px] uppercase tracking-[0.2em] ${light ? "text-zinc-300" : "text-zinc-500"}`}>
        <span className="text-gold">{post.category}</span>
        <span>{post.date}</span>
        <span>{post.readTime}</span>
    </div>
);

export default function Blog() {
    const [featured, second, ...rest] = BLOGS;

    return (
        <>
            <section data-testid="blog-hero" className="relative overflow-hidden">
                <div className="pointer-events-none absolute -top-40 left-[-10%] h-[28rem] w-[28rem] rounded-full bg-[#D4AF37]/10 blur-3xl" />
                <div className="mx-auto max-w-7xl px-6 pt-40 pb-16 md:px-10 md:pt-48 md:pb-20">
                    <MaskedLine delay={0.05}>
                        <Eyebrow>Insights & research</Eyebrow>
                    </MaskedLine>
                    <h1 className="mt-8 max-w-4xl font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-balance">
                        <MaskedLine delay={0.2}>Notes from</MaskedLine>
                        <MaskedLine delay={0.34}>
                            the <span className="font-editorial italic font-light text-gold">desk.</span>
                        </MaskedLine>
                    </h1>
                    <Reveal delay={0.6} className="mt-8 max-w-2xl">
                        <p className="text-base md:text-lg leading-relaxed text-zinc-400">
                            Researched briefings on algorithmic trading, commodities, indices, ETFs and the
                            discipline that ties them together.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section data-testid="blog-grid" className="mx-auto max-w-7xl px-6 pb-28 md:px-10 md:pb-40">
                <div className="grid gap-6 md:grid-cols-12">
                    <Reveal className="md:col-span-8">
                        <Link
                            to={`/blog/${featured.slug}`}
                            data-testid={`blog-card-${featured.slug}`}
                            className="group relative block h-full overflow-hidden border border-white/10"
                        >
                            <img
                                src={featured.image}
                                alt={featured.title}
                                className="h-[420px] md:h-full w-full object-cover opacity-70 transition-transform duration-700 ease-out group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/30 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                                <Meta post={featured} light />
                                <h2 className="mt-4 max-w-2xl font-display text-2xl md:text-4xl font-extrabold tracking-tight text-white">
                                    {featured.title}
                                </h2>
                                <p className="mt-4 max-w-xl text-sm leading-relaxed text-zinc-300">
                                    {featured.excerpt}
                                </p>
                                <span className="mt-6 inline-flex items-center gap-2 text-sm text-gold transition-transform duration-300 group-hover:translate-x-1">
                                    Read the briefing <ArrowUpRight size={15} />
                                </span>
                            </div>
                        </Link>
                    </Reveal>

                    <Reveal delay={0.1} className="md:col-span-4">
                        <Link
                            to={`/blog/${second.slug}`}
                            data-testid={`blog-card-${second.slug}`}
                            className="group flex h-full flex-col overflow-hidden border border-white/10 bg-coal/40"
                        >
                            <div className="overflow-hidden">
                                <img
                                    src={second.image}
                                    alt={second.title}
                                    className="h-52 w-full object-cover opacity-75 transition-transform duration-700 ease-out group-hover:scale-105"
                                />
                            </div>
                            <div className="flex flex-1 flex-col p-7">
                                <Meta post={second} />
                                <h3 className="mt-4 font-display text-xl font-extrabold tracking-tight">
                                    {second.title}
                                </h3>
                                <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-zinc-400">
                                    {second.excerpt}
                                </p>
                                <span className="mt-auto pt-6 inline-flex items-center gap-2 text-sm text-gold transition-transform duration-300 group-hover:translate-x-1">
                                    Read <ArrowUpRight size={14} />
                                </span>
                            </div>
                        </Link>
                    </Reveal>

                    {rest.map((post, i) => (
                        <Reveal key={post.slug} delay={0.06 * (i % 3)} className="md:col-span-4">
                            <Link
                                to={`/blog/${post.slug}`}
                                data-testid={`blog-card-${post.slug}`}
                                className="group flex h-full flex-col overflow-hidden border border-white/10 bg-coal/40 transition-colors duration-300 hover:border-gold/30"
                            >
                                <div className="overflow-hidden">
                                    <img
                                        src={post.image}
                                        alt={post.title}
                                        loading="lazy"
                                        className="h-48 w-full object-cover opacity-75 transition-transform duration-700 ease-out group-hover:scale-105"
                                    />
                                </div>
                                <div className="flex flex-1 flex-col p-7">
                                    <Meta post={post} />
                                    <h3 className="mt-4 font-display text-lg font-extrabold tracking-tight">
                                        {post.title}
                                    </h3>
                                    <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-zinc-400">
                                        {post.excerpt}
                                    </p>
                                    <span className="mt-auto pt-6 inline-flex items-center gap-2 text-sm text-gold transition-transform duration-300 group-hover:translate-x-1">
                                        Read <ArrowUpRight size={14} />
                                    </span>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>
        </>
    );
}
